import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';

import BackButton from '../../../../components/back-button/back-button';
import FitmentAddVehicleOptions from './components/fitment-add-vehicle-options/fitment-add-vehicle-options';
import FitmentCantFindThatsOk from './components/fitment-cant-find-thats-ok/fitment-cant-find-thats-ok';
import FitmentCantFindTrim from './components/fitment-cant-find-trim/fitment-cant-find-trim';
import FitmentLicenseLookup from './components/fitment-license-lookup/fitment-license-lookup';
import FitmentNoResultsFound from './components/fitment-no-results-found/fitment-no-results-found';
import FitmentSelect from './components/fitment-select/fitment-select';
import FitmentTireSize from './components/fitment-tire-size/fitment-tire-size';
import { LightBulbRegularIcon } from '../../../../shared/icons/icons';
import Collapsible from '../../../collapsible/collapsible';
import Typography from '../../../typography/typography';
import VehicleDescription from '../../../vehicle-description/vehicle-description';
import {
  CANT_FIND_OPTION_IDS,
  CRUMBS,
  EVENTS,
  I_CANT_FIND_SIZE_DROPDOWN_OPTION,
  OPTIONAL_TIRE_SIZE_DROPDOWN_OPTION,
  OPTIONAL_TIRE_SIZE_ID,
  PROP_TYPES
} from '../../fitment-constants';
import FitmentCrumbs from '../fitment-crumbs/fitment-crumbs';
import FitmentEditSteps from '../fitment-edit-steps/fitment-edit-steps';
import { default as FitmentRecentVehiclesDefault } from '../fitment-recent-vehicles/fitment-recent-vehicles';
import { default as FitmentRecentVehiclesRedesign } from '../fitment-recent-vehicles/fitment-recent-vehicles.redesign';
import {
  assemblyPropType,
  modelPropType,
  rideHeightPropType,
  trimPropType
} from './fitment-by-vehicle-util';

import {
  useFeatureFlags,
  useForm,
  useMediaQuery,
  usePrevious
} from '../../../../shared/hooks/hooks';
import { fieldTypes } from '../../../../shared/hooks/use-form/use-form';
import useFitmentSize from './hooks/use-fitment-size/use-fitment-size';

import { getInitialCrumb } from '../../../../shared/utils/fitment/fitment';

import { FEATURE_FLAGS } from '../../../../shared/constants/feature-flags';
import {
  ATV_VEHICLE_TYPE,
  STOCKHEIGHT_ID
} from '../../../../shared/constants/fitment';

import './fitment-by-vehicle.scss';

function FitmentByVehicle(props) {
  const {
    assemblies,
    canToggleVehicle,
    defaultVehicleLicensePlateAddress,
    getFitment,
    getOptionalSizes,
    hideBackButton,
    hideDelete,
    hideEditLink,
    hideLicensePlate = false,
    hideTitle = false,
    hideVehicleCrumbs = false,
    isCustomDriverTypeShowing,
    isEditing,
    isLicenseFitmentCalled,
    isLicensePlateModalEmbedded,
    isLicensePlateSizeSearch,
    isLoggedIn,
    isOptionsMenuOpenByDefault,
    isShowingRecentFitments,
    isTreadwellStart,
    isViewingOptionalSizes,
    isYearAfterModel,
    makes,
    models,
    onAssemblySelected,
    onBackToMyVehicles,
    onCrumbSelected,
    onCustomTireSizeSelected,
    onEditFitment,
    onEvent,
    onFitment,
    onFitmentSelected,
    onFitmentsToggled,
    onFocusDropdown,
    onFocusLicensePlateDropdown,
    onLicensePlateCancel,
    onLicensePlateVehicleConfirmed,
    onMakeSelected,
    onModelSelected,
    onRideHeightSelected,
    onSelectFitment,
    onShopBySelected,
    onShopProducts,
    onSubmitLicensePlate,
    onTrimSelected,
    onYearSelected,
    recentFitments,
    renderAddNewVehicleButton,
    renderDriverTypesInput,
    renderFitmentBanner,
    renderSavedFitmentsMessage,
    renderSelectedFitment,
    renderShopProductsButton,
    renderZipCodeInput,
    rideHeights,
    savedFitments,
    selectedAssembly,
    selectedCrumb,
    selectedDriverType,
    selectedFitment,
    selectedMake,
    selectedModel,
    selectedOeOption,
    selectedRideHeight,
    selectedTab,
    selectedTrim,
    selectedYear,
    setIsLicenseFitmentCalled,
    setIsLicensePlateSizeSearch,
    setIsShowingRecentFitments,
    setIsViewingOptionalSizes,
    setSelectedOeOption,
    shouldAutofocusLicensePlate,
    shouldAutofocusYear,
    shouldCallShopProducts,
    showAddVehicleOptions,
    showCustomOptions = false,
    showEmptyRecentOrSavedFitments = false,
    showShopProductsButton,
    temporaryFitment,
    textOverrides,
    trims,
    years,
    zipCodeValue
  } = props;
  const initialCrumb = getInitialCrumb();
  const fieldConfig = [
    {
      ...fieldTypes.text,
      name: 'licensePlate',
      value: ''
    },
    {
      ...fieldTypes.country,
      value: defaultVehicleLicensePlateAddress?.country
    },
    {
      ...fieldTypes.state,
      value: defaultVehicleLicensePlateAddress?.state
    }
  ];

  const {
    fields: licensePlateFields,
    formIsValid,
    updateField,
    validateAll
  } = useForm(fieldConfig);
  const { country, licensePlate, state } = licensePlateFields;

  const { isMinMd } = useMediaQuery();
  const selectMakeRef = useRef(null);
  const selectModelRef = useRef(null);
  const previousSavedFitments = usePrevious(savedFitments);
  const previousSelectedFitment = usePrevious(selectedFitment);
  const wasFitmentUnselected = previousSelectedFitment && !selectedFitment;
  const [showHelpContent, setShowHelpContent] = useState(false);
  const [iCantFindCrumb, setICantFindCrumb] = useState(null);
  const [showSizeHelp, setShowSizeHelp] = useState(false);
  const [showAddLicense, setShowAddLicense] = useState(false);

  const {
    isLoadingOptionalSizes,
    isOptionalSizesOpen,
    optionalSizes,
    selectAssembly,
    selectOptionalSize,
    selectedOptionalSize,
    selectedOptionalSizeGrouping,
    setIsOptionalSizesOpen,
    setSelectedOptionalSize,
    setSelectedOptionalSizeGrouping,
    shopProducts,
    staggeredAssemblies,
    standardAssemblies
  } = useFitmentSize({
    assemblies,
    getOptionalSizes,
    isEditing,
    isYearAfterModel,
    onEvent,
    onShopProducts,
    selectedAssembly,
    selectedFitment,
    selectedModel,
    selectedRideHeight,
    selectedTrim,
    selectedYear,
    setIsViewingOptionalSizes,
    setSelectedAssembly: onAssemblySelected,
    shouldCallShopProducts
  });

  const { featureFlags } = useFeatureFlags();
  const isFitmentRedesign = Boolean(
    featureFlags?.[FEATURE_FLAGS.__FF_FITMENT_REDESIGN__]
  );
  const isMobileVehicleCrumb = !isMinMd && selectedCrumb === CRUMBS.VEHICLE;

  function getCustomOptions(options, cantFindOptionId) {
    const mappedOptions =
      options?.map(value => ({
        label: value.description,
        value: value.id
      })) || [];
    if (showCustomOptions) {
      return [
        ...mappedOptions,
        { label: 'I can’t find it', value: cantFindOptionId }
      ];
    } else {
      return mappedOptions;
    }
  }

  const yearOptions = getCustomOptions(years, CANT_FIND_OPTION_IDS.YEAR);

  const makesRemapped =
    makes?.map(value => ({ description: value, id: value })) || [];

  const makeOptions = getCustomOptions(
    makesRemapped,
    CANT_FIND_OPTION_IDS.MAKE
  );

  const modelOptions = getCustomOptions(models, CANT_FIND_OPTION_IDS.MODEL);

  const trimOptions = getCustomOptions(trims, CANT_FIND_OPTION_IDS.TRIM);

  const [shouldShowTrimHelp, setShouldShowTrimHelp] = useState(false);
  const [shouldShowThatsOkSection, setShouldShowThatsOkSection] =
    useState(false);
  const [pendingFitment, setPendingFitment] = useState({});
  const [showAddVehicleManually, setShowAddVehicleManually] = useState(false);
  const [showAddVehicleButtons, setShowAddVehicleButtons] = useState(true);

  function getSelectedOption(options, selectedOption, optionId) {
    const selectedValue = selectedOption?.id || selectedOption;

    if (showCustomOptions) {
      return options?.find(
        option =>
          option.value ===
          (shouldShowThatsOkSection || shouldShowTrimHelp
            ? optionId
            : selectedValue)
      );
    } else {
      return options?.find(option => option.value === selectedValue);
    }
  }

  const selectedYearOption = getSelectedOption(
    yearOptions,
    selectedYear,
    CANT_FIND_OPTION_IDS.YEAR
  );

  const selectedMakeOption = getSelectedOption(
    makeOptions,
    selectedMake,
    CANT_FIND_OPTION_IDS.MAKE
  );

  const selectedModelOption = getSelectedOption(
    modelOptions,
    selectedModel,
    CANT_FIND_OPTION_IDS.MODEL
  );

  const selectedTrimOption = getSelectedOption(
    trimOptions,
    selectedTrim,
    CANT_FIND_OPTION_IDS.TRIM
  );

  const rideHeightOptions = useMemo(
    () =>
      rideHeights?.map(rideHeight => ({
        label: rideHeight.description,
        value: rideHeight.id
      })),
    [rideHeights]
  );
  const selectedRideHeightOption = rideHeightOptions?.find(
    rideHeightOption => rideHeightOption.value === selectedRideHeight?.id
  );

  const handleBackToMyVehicles = useCallback(() => {
    if (showAddVehicleOptions) {
      setShowAddVehicleManually(false);
      setShowAddLicense(false);
      setShowAddVehicleButtons(true);

      if (selectedCrumb && (!showAddVehicleButtons || showAddVehicleManually)) {
        return false;
      }
    }
    onEvent({
      name: EVENTS.BACK_TO_MY_VEHICLES
    });
    onFitmentsToggled?.(true);
    setShouldShowThatsOkSection(false);
    setICantFindCrumb(null);
    if (onBackToMyVehicles) {
      onBackToMyVehicles();
    }
    setShouldShowThatsOkSection(false);
  }, [
    onBackToMyVehicles,
    onEvent,
    onFitmentsToggled,
    showAddVehicleOptions,
    selectedCrumb,
    showAddVehicleButtons,
    showAddVehicleManually
  ]);

  const hasRecentFitments =
    Array.isArray(recentFitments) && recentFitments.length > 0;

  const hasSavedFitments =
    Array.isArray(savedFitments) && savedFitments.length > 0;
  const isOnlyShowingRecentFitments =
    isShowingRecentFitments &&
    (showEmptyRecentOrSavedFitments || hasRecentFitments || hasSavedFitments);
  const isShowingFitmentCrumbs = showAddVehicleOptions
    ? (Boolean(selectedCrumb) &&
        !showAddVehicleButtons &&
        showAddVehicleManually) ||
      isEditing
    : (Boolean(selectedCrumb) || isEditing) &&
      !hideVehicleCrumbs &&
      !shouldShowThatsOkSection;
  const isYearCompleted = Boolean(selectedYear);
  const isMakeCompleted = Boolean(selectedMake);
  const isModelCompleted = Boolean(selectedModel);
  const isTrimCompleted = Boolean(selectedTrim);
  const isVehicleCompleted = Boolean(
    selectedYear && selectedMake && selectedModel
  );

  const vehicleCrumb = {
    description: isVehicleCompleted
      ? `${selectedYear?.description} ${selectedMake} ${selectedModel?.description}`
      : '',
    isActive: Boolean(selectedYear || selectedMake || selectedModel),
    isClickable: true,
    isCompleted: isVehicleCompleted,
    label: CRUMBS.VEHICLE
  };

  const yearCrumb = {
    description: isYearCompleted ? selectedYear?.description : '',
    isActive: isYearAfterModel
      ? Boolean(selectedMake) && Boolean(selectedModel)
      : Boolean(selectedYear),
    isClickable: isYearAfterModel ? Boolean(selectedModel) : true,
    isCompleted: isYearCompleted,
    label: CRUMBS.YEAR
  };

  const makeCrumb = {
    description: isMakeCompleted ? selectedMake : '',
    isActive: isYearAfterModel ? Boolean(selectedMake) : Boolean(selectedYear),
    isClickable: isYearAfterModel ? true : yearCrumb.isCompleted,
    isCompleted: isMakeCompleted,
    label: CRUMBS.MAKE
  };

  const modelCrumb = {
    description: isModelCompleted ? selectedModel?.description : '',
    isActive: isYearAfterModel
      ? Boolean(selectedMake)
      : Boolean(selectedYear) && Boolean(selectedMake),
    isClickable: makeCrumb.isCompleted,
    isCompleted: isModelCompleted,
    label: CRUMBS.MODEL
  };

  const trimCrumb = {
    description: isTrimCompleted
      ? `${selectedTrim?.description} ${
          selectedRideHeight?.id === STOCKHEIGHT_ID ||
          !selectedRideHeight?.description
            ? ''
            : selectedRideHeight?.description
        }`
      : '',
    isActive:
      Boolean(selectedYear) && Boolean(selectedMake) && Boolean(selectedModel),
    isClickable: isYearAfterModel
      ? Boolean(selectedYear)
      : Boolean(selectedModel),
    isCompleted: isTrimCompleted,
    label: CRUMBS.TRIM
  };

  const sizeCrumb = {
    isActive:
      Boolean(selectedYear) &&
      Boolean(selectedMake) &&
      Boolean(selectedModel) &&
      Boolean(selectedTrim) &&
      Boolean(selectedRideHeight),
    isClickable: trimCrumb.isCompleted,
    isCompleted: Boolean(selectedAssembly),
    label: CRUMBS.TIRE_WHEEL_SIZE
  };

  const crumbsStandard = isYearAfterModel
    ? [makeCrumb, modelCrumb, yearCrumb, trimCrumb, sizeCrumb]
    : [yearCrumb, makeCrumb, modelCrumb, trimCrumb, sizeCrumb];
  const crumbsRedesign = [vehicleCrumb, trimCrumb, sizeCrumb];
  let crumbs =
    process.env.__FF_FITMENT_VEHICLE_CRUMB__ === 'true'
      ? crumbsRedesign
      : crumbsStandard;

  if (isTreadwellStart && pendingFitment?.type !== ATV_VEHICLE_TYPE) {
    const isDrivingActive =
      sizeCrumb.isActive && Boolean(zipCodeValue) && Boolean(selectedAssembly);
    const isDriverTypeActive = Boolean(
      selectedDriverType || isCustomDriverTypeShowing
    );
    const isZipClickable =
      selectedCrumb === CRUMBS.DRIVING_LOCATION ||
      selectedCrumb === CRUMBS.DRIVING_TYPE;
    const zipCrumb = {
      isActive: isDrivingActive,
      isClickable: isZipClickable,
      isCompleted: sizeCrumb.isCompleted && Boolean(zipCodeValue),
      label: CRUMBS.DRIVING_LOCATION
    };
    const drivingTypeCrumb = {
      isActive: isDrivingActive && isDriverTypeActive,
      isClickable: selectedCrumb === CRUMBS.DRIVING_TYPE,
      isCompleted: isDrivingActive && Boolean(selectedDriverType),
      label: CRUMBS.DRIVING_TYPE
    };

    crumbs.push(zipCrumb);
    crumbs.push(drivingTypeCrumb);
  }

  const isShowingAddVehicleOptionsBackButton =
    !hideBackButton &&
    (!hasRecentFitments || !hasSavedFitments) &&
    (showAddLicense || showAddVehicleManually);

  const showOnEditForAddVehicleManually =
    !showAddVehicleOptions || showAddVehicleManually || isEditing;

  const getVehicleFitmentSelections = useCallback(
    () => ({
      make: selectedMake,
      model: selectedModel?.description,
      rideHeight: selectedRideHeight?.description,
      trim: selectedTrim?.description,
      year: selectedYear?.description
    }),
    [
      selectedMake,
      selectedModel?.description,
      selectedRideHeight?.description,
      selectedTrim?.description,
      selectedYear?.description
    ]
  );

  function handleIStillCantFindLinkClick() {
    onAssemblySelected(null);
    setShouldShowThatsOkSection(true);
    setICantFindCrumb(CRUMBS.TIRE_WHEEL_SIZE);
    setSelectedOeOption(null);
    onEvent?.({
      name: EVENTS.I_STILL_CANT_FIND_TIRE_SIZE,
      payload: getVehicleFitmentSelections()
    });
  }

  function handleICantFindYearClick() {
    onEvent({
      name: EVENTS.I_CANT_FIND_YEAR
    });
    setShouldShowThatsOkSection(true);
  }

  function handleICantFindMakeClick() {
    onEvent({
      name: EVENTS.I_CANT_FIND_MAKE,
      payload: getVehicleFitmentSelections()
    });
    setShouldShowThatsOkSection(true);
  }

  function handleICantFindModelClick() {
    onEvent({
      name: EVENTS.I_CANT_FIND_MODEL,
      payload: getVehicleFitmentSelections()
    });
    setShouldShowThatsOkSection(true);
  }

  function handleICantFindTrimClick() {
    onEvent({
      name: EVENTS.I_CANT_FIND_TRIM,
      payload: getVehicleFitmentSelections()
    });
    setShouldShowTrimHelp(true);
  }

  function clearFitmentFromCrumb(crumb) {
    switch (crumb) {
      case CRUMBS.YEAR:
        onYearSelected(null);
        break;
      case CRUMBS.MAKE:
        onMakeSelected(null);
        break;
      case CRUMBS.MODEL:
        onModelSelected(null);
        break;
      case CRUMBS.VEHICLE:
        onYearSelected(null);
        onMakeSelected(null);
        onModelSelected(null);
        break;
    }
  }

  function handleBackFromThatsOkSection() {
    setShouldShowThatsOkSection(false);
    if (iCantFindCrumb) {
      const shouldNavigateToVehicleCrumb =
        process.env.__FF_FITMENT_VEHICLE_CRUMB__ === 'true' &&
        (iCantFindCrumb === CRUMBS.YEAR ||
          iCantFindCrumb === CRUMBS.MAKE ||
          iCantFindCrumb === CRUMBS.MODEL);
      clearFitmentFromCrumb(iCantFindCrumb);
      onCrumbSelected(
        shouldNavigateToVehicleCrumb ? CRUMBS.VEHICLE : iCantFindCrumb
      );
      setICantFindCrumb(null);
    } else {
      setIsLicensePlateSizeSearch(false);
      setIsLicenseFitmentCalled(false);
      handleBackToMyVehicles();
    }
  }

  function handleSubmitLicensePlate() {
    validateAll();
    if (formIsValid) {
      onSubmitLicensePlate(
        {
          country: country?.value?.value,
          licensePlate: licensePlate?.value,
          state: state?.value?.value?.slice(3)
        },
        onYearSelected,
        onMakeSelected,
        onModelSelected,
        onTrimSelected,
        onAssemblySelected,
        onLicensePlateVehicleConfirmed,
        onLicensePlateCancel,
        getFitment,
        onFitment,
        isLicensePlateModalEmbedded
      );
    }
  }
  const handleOeOptionSelect = useCallback(
    option => {
      if (!option) return;
      if (option.value === CANT_FIND_OPTION_IDS.SIZE) {
        setShowSizeHelp(true);
        setIsOptionalSizesOpen(false);
        onAssemblySelected(null);
        setSelectedOeOption(I_CANT_FIND_SIZE_DROPDOWN_OPTION);
        selectOptionalSize(null);
        setSelectedOptionalSizeGrouping(null);
        onEvent?.({
          name: EVENTS.VEHICLE_FITMENT_ASSEMBLY_NEED_HELP
        });
        onEvent({
          name: EVENTS.I_CANT_FIND_TIRE_SIZE_OPTION_SELECTED,
          payload: getVehicleFitmentSelections()
        });
        setShowHelpContent(true);
      } else if (option.value === OPTIONAL_TIRE_SIZE_ID) {
        setShowHelpContent(false);
        onAssemblySelected(null);
        setIsOptionalSizesOpen(false);
        setSelectedOeOption(OPTIONAL_TIRE_SIZE_DROPDOWN_OPTION);
        setShowSizeHelp(false);
        selectOptionalSize(null);
        setSelectedOptionalSizeGrouping(null);
        onEvent({
          name: EVENTS.CUSTOM_TIRE_SIZE_OPTION_SELECTED,
          payload: getVehicleFitmentSelections()
        });
        onCustomTireSizeSelected?.();
      } else {
        selectAssembly({
          description: option.label,
          id: option.value,
          isOE: true
        });
        setShowSizeHelp(false);
        setIsOptionalSizesOpen(false);
        setShowHelpContent(false);
      }
    },
    [
      getVehicleFitmentSelections,
      onAssemblySelected,
      onCustomTireSizeSelected,
      onEvent,
      selectAssembly,
      selectOptionalSize,
      setIsOptionalSizesOpen,
      setSelectedOeOption,
      setSelectedOptionalSizeGrouping
    ]
  );

  const handleReturnButton = useCallback(() => {
    const previousOeOption = selectedOeOption;
    setIsViewingOptionalSizes(false);
    setSelectedOeOption(null);
    onAssemblySelected(null);
    selectOptionalSize(null);
    setSelectedOptionalSize(null);
    setSelectedOptionalSizeGrouping(null);

    if (
      previousOeOption &&
      previousOeOption.value !== OPTIONAL_TIRE_SIZE_ID &&
      previousOeOption.value !== CANT_FIND_OPTION_IDS.SIZE
    ) {
      handleOeOptionSelect({
        label: previousOeOption.label,
        value: previousOeOption.value
      });
    }
  }, [
    handleOeOptionSelect,
    onAssemblySelected,
    selectOptionalSize,
    selectedOeOption,
    setIsViewingOptionalSizes,
    setSelectedOeOption,
    setSelectedOptionalSize,
    setSelectedOptionalSizeGrouping
  ]);

  function onAddVehicleClick() {
    void onFitmentsToggled(false);
    if (licensePlate.value) {
      resetLicensePlateFormFields();
    }
    onCrumbSelected(initialCrumb);
    setICantFindCrumb(null);
    setShouldShowThatsOkSection(false);
    setShowAddLicense(false);
    setShowAddVehicleManually(false);
    setShowAddVehicleButtons(true);
  }

  function handleCrumbSelected(crumb) {
    onCrumbSelected(crumb);
    onEvent({
      name: EVENTS.VEHICLE_FITMENT_CRUMB_SELECTED,
      payload: crumb
    });

    if (crumb === initialCrumb && licensePlate.value) {
      resetLicensePlateFormFields();
    } else if (crumb === CRUMBS.DRIVING_LOCATION) {
      shopProducts();
    }
  }

  function setCountry(value) {
    updateField(state.name, '');
    updateField(country.name, value);
  }

  function resetLicensePlateFormFields() {
    updateField(licensePlate.name, '');
    updateField(state.name, defaultVehicleLicensePlateAddress.state);
    updateField(country.name, defaultVehicleLicensePlateAddress.country);
  }

  function handleLicensePlateButton() {
    setShowAddVehicleButtons(false);
    setShowAddLicense(true);
  }

  function handleNoLicenseAndVinButton() {
    setShowAddVehicleButtons(false);
    setShowAddVehicleManually(true);
  }

  useEffect(() => {
    if (showCustomOptions) {
      setShouldShowTrimHelp(false);
    }
  }, [selectedCrumb, showCustomOptions]);

  useEffect(() => {
    if (
      wasFitmentUnselected &&
      (selectedCrumb === '' ||
        selectedCrumb === CRUMBS.DRIVING_LOCATION ||
        selectedCrumb === CRUMBS.DRIVING_TYPE) &&
      typeof renderSelectedFitment === 'function'
    ) {
      handleBackToMyVehicles();
    }
  }, [
    handleBackToMyVehicles,
    renderSelectedFitment,
    selectedCrumb,
    wasFitmentUnselected
  ]);

  useEffect(() => {
    if (previousSavedFitments?.length < savedFitments?.length) {
      setIsShowingRecentFitments(true);
    }
  }, [savedFitments, previousSavedFitments, setIsShowingRecentFitments]);

  useEffect(() => {
    onEvent?.({
      name: EVENTS.VEHICLE_FITMENT_CRUMBS_SHOWING,
      payload: isShowingFitmentCrumbs && !isOnlyShowingRecentFitments
    });
  }, [isOnlyShowingRecentFitments, isShowingFitmentCrumbs, onEvent]);

  useEffect(() => {
    if (
      selectedCrumb !== CRUMBS.YEAR &&
      selectedCrumb !== CRUMBS.MAKE &&
      selectedCrumb !== CRUMBS.MODEL &&
      selectedCrumb !== CRUMBS.VEHICLE &&
      selectedCrumb !== CRUMBS.DRIVING_LOCATION &&
      selectedCrumb !== CRUMBS.DRIVING_TYPE &&
      !selectedRideHeight &&
      !isLicenseFitmentCalled &&
      Array.isArray(rideHeights)
    ) {
      if (rideHeights.length > 1) {
        onCrumbSelected(CRUMBS.TRIM);
      } else if (rideHeights.length === 1) {
        onRideHeightSelected(rideHeights[0]);
      }
    }
  }, [
    selectedCrumb,
    selectedRideHeight,
    onRideHeightSelected,
    rideHeights,
    onCrumbSelected,
    isLicenseFitmentCalled
  ]);

  useEffect(() => {
    setPendingFitment({});
  }, [selectedCrumb]);

  useEffect(() => {
    if (isEditing) {
      setIsLicensePlateSizeSearch(false);
      setIsLicenseFitmentCalled(false);
    }
  }, [isEditing, setIsLicenseFitmentCalled, setIsLicensePlateSizeSearch]);

  if (isOnlyShowingRecentFitments) {
    const FitmentRecentVehicles = isFitmentRedesign
      ? FitmentRecentVehiclesRedesign
      : FitmentRecentVehiclesDefault;

    return (
      <FitmentRecentVehicles
        addVehicleOnClick={onAddVehicleClick}
        canToggleVehicle={canToggleVehicle}
        hideDelete={hideDelete}
        hideEditLink={hideEditLink}
        isLoggedIn={isLoggedIn}
        onEditFitment={onEditFitment}
        onEvent={onEvent}
        onFitmentSelected={onFitmentSelected}
        onSelectFitment={onSelectFitment}
        recentFitments={recentFitments}
        renderAddNewVehicleButton={renderAddNewVehicleButton}
        renderSavedFitmentsMessage={renderSavedFitmentsMessage}
        savedFitments={savedFitments}
        selectedFitment={selectedFitment}
      />
    );
  }

  return (
    <>
      {(!hideBackButton ||
        isShowingFitmentCrumbs ||
        isShowingAddVehicleOptionsBackButton) && (
        <div className="fitment-by-vehicle__controls" styleName="controls">
          {(((hasRecentFitments || hasSavedFitments) && !hideBackButton) ||
            isShowingAddVehicleOptionsBackButton) && (
            <BackButton
              className="fitment-by-vehicle-container__back-button"
              onClick={() => {
                setShowAddLicense(false);
                setIsLicenseFitmentCalled(false);
                setIsLicensePlateSizeSearch(false);
                handleBackToMyVehicles();
              }}
              styleName="back-button"
              text={
                isFitmentRedesign || (showAddVehicleManually && selectedCrumb)
                  ? 'back'
                  : 'back to my vehicles'
              }
            />
          )}

          {isShowingFitmentCrumbs && !isFitmentRedesign && (
            <div
              styleName={`${
                selectedCrumb === CRUMBS.VEHICLE && showAddVehicleOptions
                  ? 'fitment-crumb-selector-container'
                  : ''
              }`}
            >
              <FitmentCrumbs
                crumbs={crumbs}
                onCrumbSelected={handleCrumbSelected}
                selectedCrumb={selectedCrumb}
                styleName={isTreadwellStart ? 'treadwell-crumbs' : ''}
              />
            </div>
          )}
        </div>
      )}
      {isFitmentRedesign && isShowingFitmentCrumbs && (
        <>
          {!hideTitle && (
            <Typography
              className="fitment-by-vehicle__fitment-title"
              variant="H3"
            >
              {isEditing ? 'Edit' : 'Add New'} Vehicle
            </Typography>
          )}
          <Typography
            className="fitment-by-vehicle__enter-vehicle-text"
            styleName="enter-vehicle-text"
            weight="black"
          >
            Enter your vehicle information.
          </Typography>
        </>
      )}
      {shouldShowThatsOkSection ? (
        <FitmentCantFindThatsOk
          onBackClick={handleBackFromThatsOkSection}
          onShopByClick={onShopBySelected}
          showBackButton={!hasRecentFitments && !hasSavedFitments}
        />
      ) : (
        <>
          {isShowingFitmentCrumbs &&
            selectedCrumb !== CRUMBS.DRIVING_LOCATION &&
            selectedCrumb !== CRUMBS.DRIVING_TYPE &&
            (selectedYear?.description || selectedMake) && (
              <>
                {isFitmentRedesign ? (
                  <FitmentEditSteps
                    crumbs={crumbs}
                    onCrumbSelected={handleCrumbSelected}
                    selectedCrumb={selectedCrumb}
                  />
                ) : (
                  <VehicleDescription
                    make={selectedMake}
                    model={selectedModel?.description}
                    rideHeight={selectedRideHeight?.description}
                    styleName="vehicle-description"
                    trim={selectedTrim?.description}
                    year={selectedYear?.description}
                  />
                )}
              </>
            )}
          {(selectedCrumb === CRUMBS.DRIVING_LOCATION ||
            selectedCrumb === CRUMBS.DRIVING_TYPE) &&
          renderFitmentBanner
            ? renderFitmentBanner?.({ fitment: selectedFitment })
            : null}

          {(!showAddVehicleOptions ||
            showAddVehicleManually ||
            (showAddVehicleOptions && isEditing)) && (
            <div
              styleName={`${
                selectedCrumb === CRUMBS.VEHICLE && showAddVehicleOptions
                  ? 'add-vehicle-options-fitment-selector'
                  : ''
              }`}
            >
              {selectedCrumb === CRUMBS.VEHICLE && !isFitmentRedesign && (
                <Typography
                  styleName="vehicle-select-heading"
                  variant="H4"
                  weight="bold"
                >
                  Enter your vehicle information.
                </Typography>
              )}

              {!showAddLicense && (
                <div
                  className="fitment-by-vehicle__vehicle-select-container"
                  styleName={
                    selectedCrumb === CRUMBS.VEHICLE
                      ? 'vehicle-select-container'
                      : ''
                  }
                >
                  {(selectedCrumb === CRUMBS.YEAR ||
                    selectedCrumb === CRUMBS.VEHICLE) && (
                    <FitmentSelect
                      hideHeading={selectedCrumb === CRUMBS.VEHICLE}
                      inputMode="numeric"
                      isDisabled={!selectedModel && isYearAfterModel}
                      isNumeric
                      name="year"
                      onChange={async yearOption => {
                        setIsLicensePlateSizeSearch(false);
                        setIsLicenseFitmentCalled(false);
                        if (yearOption.value === CANT_FIND_OPTION_IDS.YEAR) {
                          setICantFindCrumb(CRUMBS.YEAR);
                          handleICantFindYearClick();
                        } else {
                          await onYearSelected(
                            years?.find(year => year?.id === yearOption?.value)
                          );
                          isMobileVehicleCrumb &&
                            selectMakeRef?.current?.focus();
                        }
                      }}
                      onFocus={onFocusDropdown}
                      options={yearOptions}
                      placeholder="Year"
                      renderNoResultsFound={
                        showCustomOptions
                          ? () => (
                              <FitmentNoResultsFound
                                onClick={handleICantFindYearClick}
                              />
                            )
                          : undefined
                      }
                      selectedOption={selectedYearOption}
                      shouldAutofocus={shouldAutofocusYear}
                      shouldOpenMenuByDefault={isOptionsMenuOpenByDefault}
                      styleName={isYearAfterModel ? 'year-after-model' : ''}
                    />
                  )}

                  {(selectedCrumb === CRUMBS.MAKE ||
                    selectedCrumb === CRUMBS.VEHICLE) && (
                    <FitmentSelect
                      hideHeading={selectedCrumb === CRUMBS.VEHICLE}
                      isDisabled={!selectedYear && !isYearAfterModel}
                      name="make"
                      onChange={async makeOption => {
                        if (makeOption.value === CANT_FIND_OPTION_IDS.MAKE) {
                          setICantFindCrumb(CRUMBS.MAKE);
                          handleICantFindMakeClick();
                        } else {
                          await onMakeSelected(makeOption?.value);
                          isMobileVehicleCrumb &&
                            selectModelRef?.current?.focus();
                        }
                      }}
                      onFocus={onFocusDropdown}
                      options={makeOptions}
                      placeholder="Make"
                      renderNoResultsFound={
                        showCustomOptions
                          ? () => (
                              <FitmentNoResultsFound
                                onClick={handleICantFindMakeClick}
                              />
                            )
                          : undefined
                      }
                      selectRef={selectMakeRef}
                      selectedOption={selectedMakeOption}
                    />
                  )}

                  {(selectedCrumb === CRUMBS.MODEL ||
                    selectedCrumb === CRUMBS.VEHICLE) && (
                    <FitmentSelect
                      hideHeading={selectedCrumb === CRUMBS.VEHICLE}
                      isDisabled={!selectedMake}
                      name="model"
                      onChange={modelOption => {
                        if (modelOption.value === CANT_FIND_OPTION_IDS.MODEL) {
                          setICantFindCrumb(CRUMBS.MODEL);

                          handleICantFindModelClick();
                        } else {
                          onModelSelected(
                            models?.find(
                              model => model?.id === modelOption?.value
                            )
                          );
                        }
                      }}
                      onFocus={onFocusDropdown}
                      options={modelOptions}
                      placeholder="Model"
                      renderNoResultsFound={
                        showCustomOptions
                          ? () => (
                              <FitmentNoResultsFound
                                onClick={handleICantFindModelClick}
                              />
                            )
                          : undefined
                      }
                      selectRef={selectModelRef}
                      selectedOption={selectedModelOption}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          <div
            styleName={`${
              showAddVehicleOptions &&
              showAddVehicleButtons &&
              !isEditing &&
              selectedCrumb
                ? 'add-vehicle-options-container'
                : ''
            }`}
          >
            {showAddVehicleOptions &&
              showAddVehicleButtons &&
              !isEditing &&
              selectedCrumb !== '' && (
                <>
                  <Typography
                    data-testid="fitment-title-options"
                    styleName="fitment-title"
                    variant="H3"
                  >
                    Add New Vehicle
                  </Typography>
                  <div data-testid="fitment-add-vehicle-options-container">
                    <FitmentAddVehicleOptions
                      handleAddNewVehicleOptionsButton={
                        handleLicensePlateButton
                      }
                      handleNoLicenseAndVinButton={handleNoLicenseAndVinButton}
                    />
                  </div>
                </>
              )}
          </div>

          {((!hideLicensePlate &&
            !showAddVehicleOptions &&
            (selectedCrumb === CRUMBS.YEAR ||
              selectedCrumb === CRUMBS.VEHICLE)) ||
            showAddLicense) && (
            <>
              <FitmentLicenseLookup
                fields={licensePlateFields}
                onEvent={onEvent}
                onFocusLicensePlateDropdown={onFocusLicensePlateDropdown}
                onSubmitLicensePlate={handleSubmitLicensePlate}
                setCountry={setCountry}
                setShowAddLicense={setShowAddLicense}
                shouldAutofocusLicensePlate={shouldAutofocusLicensePlate}
                showAddLicense={showAddLicense}
                updateField={updateField}
              />
            </>
          )}

          {selectedCrumb === CRUMBS.TRIM && showOnEditForAddVehicleManually && (
            <div
              className="fitment-by-vehicle__trim-selection-wrapper"
              styleName="trim-selection-wrapper"
            >
              <div styleName="trim-select-block">
                <FitmentSelect
                  name="trim"
                  onChange={trimOption => {
                    if (
                      showCustomOptions &&
                      trimOption.value === CANT_FIND_OPTION_IDS.TRIM
                    ) {
                      handleICantFindTrimClick();
                      setShowHelpContent(true);
                    } else {
                      setShouldShowTrimHelp(false);
                      setShowHelpContent(false);
                      onTrimSelected(
                        trims?.find(trim => trim?.id === trimOption?.value)
                      );
                    }
                  }}
                  onFocus={onFocusDropdown}
                  onMoreInfoContentClick={
                    !showCustomOptions
                      ? () => {
                          onEvent?.({
                            name: EVENTS.VEHICLE_FITMENT_TRIM_NEED_HELP
                          });
                        }
                      : undefined
                  }
                  options={trimOptions}
                  placeholder="Trim"
                  renderNoResultsFound={
                    showCustomOptions
                      ? () => (
                          <FitmentNoResultsFound
                            onClick={() => {
                              handleICantFindTrimClick();
                              document.activeElement?.blur();
                            }}
                          />
                        )
                      : undefined
                  }
                  selectedOption={selectedTrimOption}
                />
              </div>

              <div styleName="fitment-need-help-content">
                <div
                  className="fitment-by-vehicle__help-block"
                  styleName="help-block"
                >
                  <hr styleName="divider" />
                  <div styleName="trim-help-wrapper">
                    <Collapsible
                      isOpen={showHelpContent}
                      label={
                        <div styleName="help-button-label">
                          <LightBulbRegularIcon styleName="blub-icon" />
                          <span styleName="trim-help-label">
                            Need help finding trim?
                          </span>
                        </div>
                      }
                      onToggle={isOpen => setShowHelpContent(isOpen)}
                      toggleType="CHEVRON"
                    >
                      <FitmentCantFindTrim
                        onShopByClick={onShopBySelected}
                        showICantFindSizeOption={
                          selectedTrimOption?.value ===
                          CANT_FIND_OPTION_IDS.TRIM
                        }
                      />
                    </Collapsible>
                  </div>
                  <hr styleName="divider" />
                </div>
              </div>

              {Array.isArray(rideHeightOptions) &&
                rideHeightOptions.length > 1 && (
                  <FitmentSelect
                    heading={
                      <p>
                        What is your {selectedModelOption?.label}&apos;s{' '}
                        <b>ride height</b>?
                      </p>
                    }
                    name="ride-height"
                    onChange={rideHeightOption =>
                      onRideHeightSelected(
                        rideHeights?.find(
                          rideHeight =>
                            rideHeight?.id === rideHeightOption?.value
                        )
                      )
                    }
                    options={rideHeightOptions}
                    placeholder="Ride Height"
                    selectedOption={selectedRideHeightOption}
                    subject={selectedModelOption?.label}
                  />
                )}
            </div>
          )}

          {selectedCrumb === CRUMBS.TIRE_WHEEL_SIZE &&
            showOnEditForAddVehicleManually && (
              <FitmentTireSize
                handleOeOptionSelect={handleOeOptionSelect}
                handleReturnButton={handleReturnButton}
                isEditing={isEditing}
                isLicensePlateSizeSearch={isLicensePlateSizeSearch}
                isLoadingOptionalSizes={isLoadingOptionalSizes}
                isOptionalSizesOpen={isOptionalSizesOpen}
                isViewingOptionalSizes={isViewingOptionalSizes}
                onEvent={onEvent}
                onFocusDropdown={onFocusDropdown}
                onIStillCantFindTireSizeLinkClick={
                  handleIStillCantFindLinkClick
                }
                onShopBySelected={onShopBySelected}
                optionalSizes={optionalSizes}
                pendingFitment={pendingFitment}
                renderShopProductsButton={renderShopProductsButton}
                selectOptionalSize={selectOptionalSize}
                selectedAssembly={selectedAssembly}
                selectedFitment={temporaryFitment || selectedFitment}
                selectedOeOption={selectedOeOption}
                selectedOptionalSize={selectedOptionalSize}
                selectedOptionalSizeGrouping={selectedOptionalSizeGrouping}
                selectedRideHeight={selectedRideHeight}
                selectedTab={selectedTab}
                setIsOptionalSizesOpen={setIsOptionalSizesOpen}
                setIsViewingOptionalSizes={setIsViewingOptionalSizes}
                setPendingFitment={setPendingFitment}
                setSelectedOeOption={setSelectedOeOption}
                setSelectedOptionalSizeGrouping={
                  setSelectedOptionalSizeGrouping
                }
                setShowHelpContent={setShowHelpContent}
                setShowSizeHelp={setShowSizeHelp}
                shopProducts={() => {
                  setIsLicensePlateSizeSearch(false);
                  setIsLicenseFitmentCalled(false);
                  shopProducts();
                }}
                showCustomOptions={showCustomOptions}
                showHelpContent={showHelpContent}
                showShopProductsButton={showShopProductsButton}
                showSizeHelp={showSizeHelp}
                staggeredAssemblies={staggeredAssemblies}
                standardAssemblies={standardAssemblies}
                textOverrides={textOverrides}
              />
            )}

          {selectedCrumb === CRUMBS.DRIVING_LOCATION && (
            <div styleName="driving-location">
              {renderZipCodeInput?.({
                onSubmit: () => {
                  onCrumbSelected(CRUMBS.DRIVING_TYPE);
                }
              })}
            </div>
          )}

          {selectedCrumb === CRUMBS.DRIVING_TYPE && (
            <>
              <hr styleName="hr" />
              {renderDriverTypesInput?.()}
            </>
          )}

          {selectedCrumb === '' &&
            Boolean(selectedFitment) &&
            typeof renderSelectedFitment === 'function' &&
            renderSelectedFitment()}
        </>
      )}
    </>
  );
}

FitmentByVehicle.propTypes = {
  assemblies: PropTypes.arrayOf(assemblyPropType),
  canToggleVehicle: PropTypes.bool,
  defaultVehicleLicensePlateAddress: PropTypes.object,
  getFitment: PropTypes.func,
  getOptionalSizes: PropTypes.func,
  hideBackButton: PropTypes.bool,
  hideDelete: PropTypes.bool,
  hideEditLink: PropTypes.bool,
  hideLicensePlate: PropTypes.bool,
  hideTitle: PropTypes.bool,
  hideVehicleCrumbs: PropTypes.bool,
  isCustomDriverTypeShowing: PropTypes.bool,
  isEditing: PropTypes.bool,
  isLicenseFitmentCalled: PropTypes.bool,
  isLicensePlateModalEmbedded: PropTypes.bool,
  isLicensePlateSizeSearch: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isOptionsMenuOpenByDefault: PropTypes.bool,
  isShowingRecentFitments: PropTypes.bool.isRequired,
  isTreadwellStart: PropTypes.bool,
  isViewingOptionalSizes: PropTypes.bool,
  isYearAfterModel: PropTypes.bool,
  makes: PropTypes.arrayOf(PropTypes.string),
  models: PropTypes.arrayOf(modelPropType),
  onAssemblySelected: PropTypes.func,
  onBackToMyVehicles: PropTypes.func,
  onCrumbSelected: PropTypes.func,
  onCustomTireSizeSelected: PropTypes.func,
  onEditFitment: PropTypes.func,
  onEvent: PropTypes.func,
  onFitment: PropTypes.func,
  onFitmentSelected: PropTypes.func,
  onFitmentsToggled: PropTypes.func,
  onFocusDropdown: PropTypes.func,
  onFocusLicensePlateDropdown: PropTypes.func,
  onLicensePlateCancel: PropTypes.func,
  onLicensePlateVehicleConfirmed: PropTypes.func,
  onMakeSelected: PropTypes.func,
  onModelSelected: PropTypes.func,
  onRideHeightSelected: PropTypes.func,
  onSelectFitment: PropTypes.func,
  onShopBySelected: PropTypes.func,
  onShopProducts: PropTypes.func,
  onSubmitLicensePlate: PropTypes.func,
  onTrimSelected: PropTypes.func,
  onYearSelected: PropTypes.func,
  recentFitments: PropTypes.arrayOf(PROP_TYPES.fitment),
  renderAddNewVehicleButton: PropTypes.func,
  renderDriverTypesInput: PropTypes.func,
  renderFitmentBanner: PropTypes.func,
  renderSavedFitmentsMessage: PropTypes.func,
  renderSelectedFitment: PropTypes.func,
  renderShopProductsButton: PropTypes.func,
  renderZipCodeInput: PropTypes.func,
  rideHeights: PropTypes.arrayOf(rideHeightPropType),
  savedFitments: PropTypes.arrayOf(PROP_TYPES.fitment),
  selectedAssembly: assemblyPropType,
  selectedCrumb: PropTypes.string,
  selectedDriverType: PropTypes.string,
  selectedFitment: PROP_TYPES.fitment,
  selectedMake: PropTypes.string,
  selectedModel: modelPropType,
  selectedOeOption: PropTypes.object,
  selectedRideHeight: rideHeightPropType,
  selectedTab: PropTypes.string,
  selectedTrim: trimPropType,
  selectedYear: PropTypes.any,
  setIsLicenseFitmentCalled: PropTypes.func,
  setIsLicensePlateSizeSearch: PropTypes.func,
  setIsShowingRecentFitments: PropTypes.func,
  setIsViewingOptionalSizes: PropTypes.func,
  setSelectedOeOption: PropTypes.func,
  shouldAutofocusLicensePlate: PropTypes.bool,
  shouldAutofocusYear: PropTypes.bool,
  shouldCallShopProducts: PropTypes.bool,
  showAddVehicleOptions: PropTypes.bool,
  showCustomOptions: PropTypes.bool,
  showEmptyRecentOrSavedFitments: PropTypes.bool,
  showShopProductsButton: PropTypes.bool,
  temporaryFitment: PROP_TYPES.fitment,
  textOverrides: PropTypes.object,
  trims: PropTypes.arrayOf(trimPropType),
  years: PropTypes.any,
  zipCodeValue: PropTypes.string
};

export default FitmentByVehicle;
